import { ComponentProps, FC } from "react";
import { Col, Row, Space, Skeleton } from "antd";
import * as _ from "lodash";

import { DataAvailability, MissingDataReason, Direction } from "@ctra/api";
import { classname, Nullable } from "@ctra/utils";

import { Typography } from "../../../elements";
import Bar from "../Bar";
import Arrow from "../Arrow";
import ValueChange from "../ValueChange";
import WidgetWrapper from "../WidgetWrapper";

import "./KPIChange.css";
import styles from "./KPIChange.module.less";

const { Text } = Typography;

export enum Variant {
  bars = "bars",
  table = "table"
}

export interface KPIChangeProps extends ComponentProps<typeof WidgetWrapper> {
  labels: {
    kpi: string;
    direction: string;
    noData?: string;
  };
  before: {
    label?: string;
    value: Nullable<number>;
    noData?: string;
  };
  expected?: {
    label?: string;
    value: Nullable<number>;
    noData?: string;
  };
  after: {
    label?: string;
    value: Nullable<number>;
    noData?: string;
  };
  suffix: string;
  direction: Direction;
  variant?: Variant;
  value?: number;
  loading?: boolean;
  context?: {
    dataStatus: Nullable<DataAvailability>;
    missingDataReason: Nullable<MissingDataReason>;
  };
}

/**
 * Bar representation of a KPI change
 * @param {{kpi: string, direction: string, noData?: string}} labels
 * @param {{label?: string, value: Nullable<number>}} before
 * @param {{label?: string, value: Nullable<number>} | undefined} expected
 * @param {{label?: string, value: Nullable<number>}} after
 * @param {string} suffix
 * @param {Direction} direction
 * @param {number | undefined} inputValue
 * @param {string | undefined} className
 * @param {boolean | undefined} loading
 * @param {{dataAvailability: Nullable<DataAvailability>, dataUnavailableReason: Nullable<MissingDataReason>} | undefined} context
 * @param {Omit<React.PropsWithChildren<Omit<KPIChangeProps, "variant" | "label">>, "before" | "expected" | "context" | "className" | "after" | "suffix" | "loading" | "value" | "labels" | "direction">} rest
 * @returns {JSX.Element}
 * @constructor
 */
const BarVariant: FC<Omit<KPIChangeProps, "variant" | "label">> = ({
  labels,
  before,
  expected,
  after,
  suffix,
  direction,
  value: inputValue,
  className,
  loading,
  context,
  ...rest
}) => {
  /**
   * Calculate the delta between the before and after values
   * @type {number | null}
   */
  const delta =
    after.value && _.defaultTo(expected, before).value
      ? _.round(Math.abs((_.defaultTo(expected, before).value as NonNullable<number>) - after.value), 2)
      : null;

  /**
   * Calculate the value to display
   * @type {number | null}
   */
  const value = _.defaultTo(inputValue, delta);

  /**
   * Calculate the min value
   * @type {number}
   */
  const min = Math.min(..._.compact([before.value, expected?.value, after.value]));

  /**
   * Tell if any of the values are negative
   * @type {boolean}
   */
  const mixed = _.some<number>(_.compact([before.value, expected?.value, after.value]), (v) => v < 0);

  /**
   * Shift/adjust values to be positive
   * @type {(number|undefined)[]}
   */
  const [avBefore, avExpected, avAfter] = _.map([before.value, expected?.value, after.value], (v) =>
    mixed && _.isNumber(v) ? v + Math.abs(min) : v
  );

  /**
   * Calculate the max value
   * @type {number}
   */
  const max = Math.max(..._.compact([avBefore, avExpected, avAfter]));

  return (
    <WidgetWrapper label={labels.kpi} className={classname(styles.Wrapper, className)} {...rest}>
      <Skeleton active loading={loading}>
        <Row wrap={false} align="middle">
          <Col className={styles.Bars} flex={1}>
            <Row gutter={[16, 0]}>
              <Col style={{ width: "85px" }}>
                <Row align="middle" style={{ height: "100%" }} className={styles.Text}>
                  <Col span={24} style={{ height: expected ? "33%" : "50%" }}>
                    <Text size="md">{before.label}</Text>
                  </Col>
                  {expected && (
                    <Col span={24} style={{ height: "34%" }}>
                      <Text size="md">{expected.label}</Text>
                    </Col>
                  )}
                  <Col span={24} style={{ height: expected ? "33%" : "50%" }}>
                    <Text size="md">{after.label}</Text>
                  </Col>
                </Row>
              </Col>
              <Col flex={1}>
                <Bar
                  value={_.defaultTo(before.value, before.noData)}
                  unit={suffix}
                  variant={before.value ? "light" : "lightGray"}
                  width={_.isNumber(avBefore) ? (avBefore / max) * 100 : void 0}
                />
                {expected && _.isNumber(avExpected) && (
                  <Bar variant="neon" value={expected.value} unit={suffix} width={(avExpected / max) * 100} />
                )}
                <Bar
                  variant={after.value ? "dark" : "darkGray"}
                  value={_.defaultTo(after.value, after.noData)}
                  unit={suffix}
                  width={_.isNumber(avAfter) ? (avAfter / max) * 100 : void 0}
                />
              </Col>
            </Row>
          </Col>
          <Col className={classname("ctra-impactTracking-kpiChange-metricWrapper", styles.MetricWrapper)}>
            <ValueChange
              type={before.value && after.value ? direction : void 0}
              value={value}
              suffix={suffix}
              text={_.isNumber(value) ? labels.direction : labels.noData}
              context={context}
            />
          </Col>
        </Row>
      </Skeleton>
    </WidgetWrapper>
  );
};

/**
 * Table variant of a KPI change
 * @param {{kpi: string, direction: string, noData?: string}} labels
 * @param {{label?: string, value: Nullable<number>}} before
 * @param {string} suffix
 * @param {{label?: string, value: Nullable<number>} | undefined} expected
 * @param {{label?: string, value: Nullable<number>}} after
 * @param {Direction} direction
 * @param {string | undefined} className
 * @return {JSX.Element}
 * @constructor
 */
const TableRowVariant: FC<Omit<KPIChangeProps, "variant" | "label">> = ({
  labels,
  before,
  suffix,
  expected,
  after,
  direction,
  className
}) => (
  <Row wrap={false} className={classname(className)}>
    <Col span={6}>
      <Text strong className={styles.KpiCell}>
        {labels.kpi}
      </Text>
    </Col>
    <Col span={6}>
      <Text>
        {before.value} {suffix}
      </Text>
    </Col>
    <Col span={6}>
      <Text>
        {after.value} {suffix}
      </Text>
    </Col>
    <Col span={6}>
      <Space>
        <Arrow size="small" direction={direction} />
        {_.isNumber(after.value) && _.isNumber(_.defaultTo(expected, before).value) && (
          <Space size="large">
            <Text>
              {_.round(
                Math.abs((_.defaultTo(expected, before).value as NonNullable<number>) - after.value),
                2
              )}{" "}
              {suffix}
            </Text>
            {labels.direction}
          </Space>
        )}
      </Space>
    </Col>
  </Row>
);

/**
 * KPI value change before/after
 * @param {Variant | undefined} variant
 * @param {Pick<KPIChangeProps & {children?: React.ReactNode | undefined}, "labels" | "before" | "after" | "suffix" | "direction" | "gutter" | "align" | "justify" | "prefixCls" | "wrap" | "defaultChecked" | "defaultValue" | "suppressContentEditableWarning" | "suppressHydrationWarning" | "accessKey" | "className" | "contentEditable" | "contextMenu" | "dir" | "draggable" | "hidden" | "id" | "lang" | "placeholder" | "slot" | "spellCheck" | "style" | "tabIndex" | "title" | "translate" | "radioGroup" | "role" | "about" | "datatype" | "inlist" | "prefix" | "property" | "resource" | "typeof" | "vocab" | "autoCapitalize" | "autoCorrect" | "autoSave" | "color" | "itemProp" | "itemScope" | "itemType" | "itemID" | "itemRef" | "results" | "security" | "unselectable" | "inputMode" | "is" | "aria-activedescendant" | "aria-atomic" | "aria-autocomplete" | "aria-busy" | "aria-checked" | "aria-colcount" | "aria-colindex" | "aria-colspan" | "aria-controls" | "aria-current" | "aria-describedby" | "aria-details" | "aria-disabled" | "aria-dropeffect" | "aria-errormessage" | "aria-expanded" | "aria-flowto" | "aria-grabbed" | "aria-haspopup" | "aria-hidden" | "aria-invalid" | "aria-keyshortcuts" | "aria-label" | "aria-labelledby" | "aria-level" | "aria-live" | "aria-modal" | "aria-multiline" | "aria-multiselectable" | "aria-orientation" | "aria-owns" | "aria-placeholder" | "aria-posinset" | "aria-pressed" | "aria-readonly" | "aria-relevant" | "aria-required" | "aria-roledescription" | "aria-rowcount" | "aria-rowindex" | "aria-rowspan" | "aria-selected" | "aria-setsize" | "aria-sort" | "aria-valuemax" | "aria-valuemin" | "aria-valuenow" | "aria-valuetext" | "children" | "dangerouslySetInnerHTML" | "onCopy" | "onCopyCapture" | "onCut" | "onCutCapture" | "onPaste" | "onPasteCapture" | "onCompositionEnd" | "onCompositionEndCapture" | "onCompositionStart" | "onCompositionStartCapture" | "onCompositionUpdate" | "onCompositionUpdateCapture" | "onFocus" | "onFocusCapture" | "onBlur" | "onBlurCapture" | "onChange" | "onChangeCapture" | "onBeforeInput" | "onBeforeInputCapture" | "onInput" | "onInputCapture" | "onReset" | "onResetCapture" | "onSubmit" | "onSubmitCapture" | "onInvalid" | "onInvalidCapture" | "onLoad" | "onLoadCapture" | "onError" | "onErrorCapture" | "onKeyDown" | "onKeyDownCapture" | "onKeyPress" | "onKeyPressCapture" | "onKeyUp" | "onKeyUpCapture" | "onAbort" | "onAbortCapture" | "onCanPlay" | "onCanPlayCapture" | "onCanPlayThrough" | "onCanPlayThroughCapture" | "onDurationChange" | "onDurationChangeCapture" | "onEmptied" | "onEmptiedCapture" | "onEncrypted" | "onEncryptedCapture" | "onEnded" | "onEndedCapture" | "onLoadedData" | "onLoadedDataCapture" | "onLoadedMetadata" | "onLoadedMetadataCapture" | "onLoadStart" | "onLoadStartCapture" | "onPause" | "onPauseCapture" | "onPlay" | "onPlayCapture" | "onPlaying" | "onPlayingCapture" | "onProgress" | "onProgressCapture" | "onRateChange" | "onRateChangeCapture" | "onSeeked" | "onSeekedCapture" | "onSeeking" | "onSeekingCapture" | "onStalled" | "onStalledCapture" | "onSuspend" | "onSuspendCapture" | "onTimeUpdate" | "onTimeUpdateCapture" | "onVolumeChange" | "onVolumeChangeCapture" | "onWaiting" | "onWaitingCapture" | "onAuxClick" | "onAuxClickCapture" | "onClick" | "onClickCapture" | "onContextMenu" | "onContextMenuCapture" | "onDoubleClick" | "onDoubleClickCapture" | "onDrag" | "onDragCapture" | "onDragEnd" | "onDragEndCapture" | "onDragEnter" | "onDragEnterCapture" | "onDragExit" | "onDragExitCapture" | "onDragLeave" | "onDragLeaveCapture" | "onDragOver" | "onDragOverCapture" | "onDragStart" | "onDragStartCapture" | "onDrop" | "onDropCapture" | "onMouseDown" | "onMouseDownCapture" | "onMouseEnter" | "onMouseLeave" | "onMouseMove" | "onMouseMoveCapture" | "onMouseOut" | "onMouseOutCapture" | "onMouseOver" | "onMouseOverCapture" | "onMouseUp" | "onMouseUpCapture" | "onSelect" | "onSelectCapture" | "onTouchCancel" | "onTouchCancelCapture" | "onTouchEnd" | "onTouchEndCapture" | "onTouchMove" | "onTouchMoveCapture" | "onTouchStart" | "onTouchStartCapture" | "onPointerDown" | "onPointerDownCapture" | "onPointerMove" | "onPointerMoveCapture" | "onPointerUp" | "onPointerUpCapture" | "onPointerCancel" | "onPointerCancelCapture" | "onPointerEnter" | "onPointerEnterCapture" | "onPointerLeave" | "onPointerLeaveCapture" | "onPointerOver" | "onPointerOverCapture" | "onPointerOut" | "onPointerOutCapture" | "onGotPointerCapture" | "onGotPointerCaptureCapture" | "onLostPointerCapture" | "onLostPointerCaptureCapture" | "onScroll" | "onScrollCapture" | "onWheel" | "onWheelCapture" | "onAnimationStart" | "onAnimationStartCapture" | "onAnimationEnd" | "onAnimationEndCapture" | "onAnimationIteration" | "onAnimationIterationCapture" | "onTransitionEnd" | "onTransitionEndCapture" | "ref" | "key">} rest
 * @return {JSX.Element}
 * @constructor
 */
const KPIChange: FC<Omit<KPIChangeProps, "label">> = ({ variant = Variant.bars, ...rest }) =>
  variant === Variant.bars ? <BarVariant {...rest} /> : <TableRowVariant {...rest} />;

export default KPIChange;
